import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Factory, FlaskConical, Leaf, PackageCheck, ShieldCheck } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { QualityDocs } from "../components/QualityDocs";
import { IMAGES, CATEGORIES } from "../data/products";

const MANIFESTO = [
  {
    no: "01",
    icon: Factory,
    title: "Manufactured In-House",
    body: "Based inside G.I.D.C. Vatva, Ahmedabad — every powder milled, blended and packed under our own roof. No outsourcing, no surprises.",
  },
  {
    no: "02",
    icon: FlaskConical,
    title: "Batch-Tested Consistency",
    body: "Colour strength, mesh size and moisture are checked on every lot. What you approved in the sample is what arrives at your plant — every single time.",
  },
  {
    no: "03",
    icon: PackageCheck,
    title: "Built For Export",
    body: "From 25 kg industrial sacks to custom private-label packing, we ship food-grade material to processors and brands across borders with full documentation.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden pt-[72px]">
        <div className="absolute inset-0 z-0">
          <motion.img
            src={IMAGES.hero}
            alt="Golden caramel fluid on black"
            style={{ y: imgY, scale: imgScale }}
            className="w-full h-[115%] object-cover object-center will-change-transform"
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/60" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 sm:px-10 pb-20 sm:pb-28 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="overline-label mb-8"
            data-testid="hero-overline"
          >
            Manufacturers &amp; Exporters — Food Powders
          </motion.p>

          <h1 className="font-display font-black tracking-tighter leading-[0.88] text-[clamp(3.2rem,11vw,10rem)]" data-testid="hero-heading">
            <MaskedLine delay={0.25}>PURE POWDERS.</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97736] via-[#F5B82A] to-[#F25C05]">INDUSTRIAL</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>PRECISION.</MaskedLine>
          </h1>

          <div className="mt-12 flex flex-col sm:flex-row sm:items-end justify-between gap-10">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-md text-base sm:text-lg leading-relaxed text-[#f5f5f5]/70"
              data-testid="hero-subtext"
            >
              MSD Foods offers caramel colours, food flavour powders and functional
              ingredients for processors who can't afford inconsistency.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-4"
            >
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="group flex items-center gap-3 bg-[#D97736] text-[#0a0a0a] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#e8904f]"
              >
                Explore Products
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="group flex items-center gap-3 border border-white/25 text-[#f5f5f5] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-8 py-4 rounded-full transition-colors duration-300 hover:border-[#D97736] hover:text-[#D97736]"
              >
                Get A Quote
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* FEATURED BENTO */}
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 py-24 sm:py-36" data-testid="featured-section">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16">
            <div>
              <p className="overline-label mb-4">The Catalogue</p>
              <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-none">
                Three lines.<br />One standard.
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="featured-view-all"
              className="group flex items-center gap-2 font-mono-jb text-xs uppercase tracking-[0.2em] text-[#f5f5f5]/70 hover:text-[#D97736] transition-colors duration-300"
            >
              View full catalogue
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-7" delay={0}>
            <Link to="/products" data-testid="bento-caramel" className="group relative block h-[420px] md:h-[520px] overflow-hidden clip-frame bg-[#111]">
              <img src={IMAGES.hero} alt="Caramel colours" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/40 transition-colors duration-500 group-hover:bg-black/25" />
              <div className="absolute bottom-0 left-0 p-8 sm:p-10">
                <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#D97736] mb-3">CC / 01—04</p>
                <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Caramel Colours</h3>
                <p className="mt-2 text-sm text-[#f5f5f5]/70 max-w-sm">Type I–IV in powder &amp; liquid, for beverages, bakery and sauces.</p>
              </div>
            </Link>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.1}>
            <Link to="/products" data-testid="bento-flavours" className="group relative block h-[420px] md:h-[520px] overflow-hidden clip-frame bg-[#111]">
              <img src={IMAGES.spices} alt="Food flavours" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/40 transition-colors duration-500 group-hover:bg-black/25" />
              <div className="absolute bottom-0 left-0 p-8 sm:p-10">
                <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#D97736] mb-3">FF / 01—11</p>
                <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Food Flavours</h3>
                <p className="mt-2 text-sm text-[#f5f5f5]/70 max-w-xs">Eleven dehydrated vegetable &amp; herb powders, milled to order.</p>
              </div>
            </Link>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.15}>
            <Link to="/products" data-testid="bento-ingredients" className="group relative block h-[380px] overflow-hidden clip-frame bg-[#111]">
              <img src={IMAGES.scoop} alt="Industrial ingredients" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/40 transition-colors duration-500 group-hover:bg-black/25" />
              <div className="absolute bottom-0 left-0 p-8 sm:p-10">
                <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#D97736] mb-3">II / 01—07</p>
                <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Industrial Ingredients</h3>
                <p className="mt-2 text-sm text-[#f5f5f5]/70 max-w-xs">Starches, sweeteners and preservatives for scale.</p>
              </div>
            </Link>
          </Reveal>
          <Reveal className="md:col-span-7" delay={0.2}>
            <div className="relative block h-[380px] overflow-hidden clip-frame bg-[#1E3F33] p-8 sm:p-12 flex flex-col justify-between" data-testid="process-tile">
              <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#D97736]">The Process</p>
              <div className="space-y-6">
                {[
                  { icon: Leaf, color: "#3E9B4F", title: "Right Quality Sourcing", body: "Vegetables & herbs picked at peak season from trusted farms." },
                  { icon: ShieldCheck, color: "#F5B82A", title: "Hygienic Production", body: "Milled, blended and packed in a controlled food-safe facility." },
                  { icon: FlaskConical, color: "#F25C05", title: "World-Class Testing", body: "Every batch verified in our testing lab before dispatch." },
                ].map((s) => (
                  <div key={s.title} className="flex items-start gap-4" data-testid={`process-step-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <s.icon className="w-6 h-6 shrink-0 mt-0.5" style={{ color: s.color }} strokeWidth={2} />
                    <div>
                      <p className="font-display text-lg sm:text-xl font-bold tracking-tight text-[#f5f5f5]">{s.title}</p>
                      <p className="text-xs sm:text-sm text-[#f5f5f5]/60">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BRAND SHOWCASE */}
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pb-24 sm:pb-36" data-testid="brand-showcase-section">
        <Reveal>
          <p className="overline-label mb-4">Straight From Our Catalogue</p>
          <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-none mb-16">
            Colour that sells<br />itself.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[
            { src: "/images/caramel-colors.png", alt: "MSD Foods Caramel Colors catalogue poster", testid: "showcase-caramel-poster" },
            { src: "/images/food-flavours.png", alt: "MSD Foods Food Flavours catalogue poster", testid: "showcase-flavours-poster" },
          ].map((p, i) => (
            <Reveal key={p.testid} delay={i * 0.12}>
              <Link to="/products" data-testid={p.testid} className="group block">
                <div className="p-2 sm:p-3 bg-white/5 border border-white/10 rounded-2xl transition-colors duration-500 group-hover:border-[#D97736]/40">
                  <div className="relative overflow-hidden rounded-xl">
                    <img
                      src={p.src}
                      alt={p.alt}
                      className="w-full h-auto transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_55%,rgba(10,10,10,0.35)_100%)]" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-t border-white/10" data-testid="manifesto-section">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-24 sm:py-36">
          <Reveal>
            <p className="overline-label mb-4">The MSD Standard</p>
            <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-none mb-20">
              A manifesto in<br />three chapters.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
            {MANIFESTO.map((ch, i) => (
              <Reveal key={ch.no} delay={i * 0.12} className="border-r border-b border-white/10">
                <div className="group p-10 sm:p-12 h-full transition-colors duration-500 hover:bg-[#111111]" data-testid={`manifesto-chapter-${ch.no}`}>
                  <div className="flex items-start justify-between mb-16">
                    <span className="font-display text-6xl sm:text-7xl font-black tracking-tighter text-outline transition-all duration-500 group-hover:text-[#D97736] group-hover:[-webkit-text-stroke:0px]">
                      {ch.no}
                    </span>
                    <ch.icon className="w-7 h-7 text-[#D97736]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl font-bold tracking-tight mb-4">{ch.title}</h3>
                  <p className="text-sm leading-relaxed text-[#f5f5f5]/60">{ch.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="border-t border-white/10" data-testid="gallery-section">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
          <Reveal>
            <p className="overline-label mb-4">From The Mill Floor</p>
            <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-none mb-16">
              Colour you can<br />almost taste.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 items-start">
            {[
              { src: IMAGES.spiceBowls, caption: "Assorted powder lots, pre-blend", span: "sm:col-span-5", h: "h-[420px] sm:h-[560px]", testid: "gallery-img-spices" },
              { src: IMAGES.spoonsPowder, caption: "Mesh-graded flavour powders", span: "sm:col-span-4", h: "h-[420px] sm:h-[440px] sm:mt-24", testid: "gallery-img-spoons" },
              { src: IMAGES.beetsDark, caption: "Raw beetroot, pre-dehydration", span: "sm:col-span-3", h: "h-[420px] sm:h-[500px] sm:mt-10", testid: "gallery-img-beets" },
            ].map((img, i) => (
              <Reveal key={img.testid} delay={i * 0.12} className={img.span}>
                <figure className="group" data-testid={img.testid}>
                  <div className={`relative overflow-hidden clip-frame ${img.h}`}>
                    <img
                      src={img.src}
                      alt={img.caption}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/20 transition-colors duration-500 group-hover:bg-black/0" />
                  </div>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="w-6 h-px bg-[#D97736]" />
                    <span className="font-mono-jb text-[10px] uppercase tracking-[0.25em] text-[#f5f5f5]/50">
                      {img.caption}
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="border-t border-white/10" data-testid="certifications-section">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
          <Reveal>
            <p className="overline-label mb-4">Compliance</p>
            <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-none mb-6">
              Certified where<br />it counts.
            </h2>
            <p className="max-w-xl text-sm sm:text-base leading-relaxed text-[#f5f5f5]/60 mb-16">
              Licensed and audited under India's food safety regime with a certified quality
              management system. Certificates available on request with every quote.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
            {[
              {
                src: "/badges/fssai.png",
                alt: "FSSAI logo",
                title: "FSSAI Licensed",
                body: "Food Safety & Standards Authority of India",
                testid: "cert-badge-fssai",
              },
              {
                src: "/badges/iso-9001-2015.svg",
                alt: "ISO 9001:2015 logo",
                title: "ISO 9001:2015",
                body: "Certified Quality Management System",
                testid: "cert-badge-iso",
              },
            ].map((cert, i) => (
              <Reveal key={cert.testid} delay={i * 0.12}>
                <div
                  className="group border border-white/10 p-8 sm:p-10 transition-colors duration-500 hover:bg-[#111111]"
                  data-testid={cert.testid}
                >
                  <div className="bg-white rounded-lg h-28 flex items-center justify-center px-8 mb-8 transition-transform duration-500 group-hover:-translate-y-1">
                    <img src={cert.src} alt={cert.alt} className="max-h-16 w-auto object-contain" />
                  </div>
                  <h3 className="font-display text-xl font-bold tracking-tight mb-1">{cert.title}</h3>
                  <p className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#f5f5f5]/50">
                    {cert.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES STRIP */}
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pb-24 sm:pb-36" data-testid="categories-strip">
        <Reveal>
          <div className="border border-white/10">
            {CATEGORIES.map((cat, i) => (
              <Link
                key={cat.id}
                to="/products"
                data-testid={`category-row-${cat.id}`}
                className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-8 sm:px-12 py-8 border-b border-white/10 last:border-b-0 transition-colors duration-500 hover:bg-[#111111]"
              >
                <div className="flex items-baseline gap-6">
                  <span className="font-mono-jb text-xs text-[#D97736]">{cat.code}</span>
                  <span className="font-display text-2xl sm:text-4xl font-bold tracking-tight transition-transform duration-500 group-hover:translate-x-2">
                    {cat.label}
                  </span>
                </div>
                <div className="flex items-center gap-6">
                  <span className="font-mono-jb text-xs text-[#f5f5f5]/50">
                    {String(cat.products.length).padStart(2, "0")} products
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-[#f5f5f5]/40 transition-all duration-300 group-hover:text-[#D97736] group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      <QualityDocs />
    </main>
  );
}
